import React, { useEffect, useRef, useId } from 'react'
import { cls } from '../../utils/helpers.js'
import { useLanguage } from '../../i18n/index.jsx'
import { ModalErrorBoundary } from '../ErrorBoundary.jsx'

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl'

export interface ModalProps {
  open?: boolean
  onClose: () => void
  title?: React.ReactNode
  children?: React.ReactNode
  size?: ModalSize
  role?: React.AriaRole
}

export function Modal({ open, onClose, title, children, size = 'md', role = 'dialog' }: ModalProps) {
  const { t } = useLanguage()
  const modalRef = useRef<HTMLDivElement | null>(null)
  const titleId = useId()

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => { document.body.style.overflow = '' }
  }, [open])

  // Capture the element that had focus before open, restore on close (WCAG 2.4.3)
  const prevFocusRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (open) {
      prevFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
      modalRef.current?.focus()
      return () => {
        const el = prevFocusRef.current
        if (el && typeof el.focus === 'function' && document.body.contains(el)) {
          // Defer so React has finished unmounting the modal before we refocus
          queueMicrotask(() => el.focus())
        }
        prevFocusRef.current = null
      }
    }
    return undefined
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return }
      // Focus trap: keep Tab/Shift+Tab within the modal
      if (e.key === 'Tab') {
        const modal = modalRef.current
        if (!modal) return
        const focusable = modal.querySelectorAll<HTMLElement>(
          'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey) {
          if (document.activeElement === first || document.activeElement === modal) {
            e.preventDefault()
            last.focus()
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault()
            first.focus()
          }
        }
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="modal-overlay" role="presentation" onClick={e => e.target === e.currentTarget && onClose()}>
      <div
        ref={modalRef}
        tabIndex={-1}
        className={cls('modal', `modal-${size}`)}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="modal-header">
          <h2 id={titleId} className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose} aria-label={t('ariaCloseDialog')}>✕</button>
        </div>
        <div className="modal-body">
          <ModalErrorBoundary>{children}</ModalErrorBoundary>
        </div>
      </div>
    </div>
  )
}
